<script lang="ts">
  interface SearchDialogItem<T> {
    id: string
    title: string
    focusable: boolean
    props?: T
    component?: Component
    selected?: (item: T) => void
  }
</script>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, type Component } from 'vue'
  import { useSearch, useRecentSearch } from '../../utils/search-providers'
  import { type RecentSearch } from '../../utils/search-types'
  import RecentSearchItem from '../recent-search/recent-search-item.vue'
  import GlobalSearchContextItem from '../global-search/global-search-context-item.vue'
  import { type GlobalSearchContextItemProps } from '../global-search/global-search-context-item.vue'
  import { type SearchContextType } from '../../utils/global-search-utils'
  import { globalUtils } from '@gat-ship/utils'

  const props = defineProps<{
    contentContainerRef: InstanceType<typeof HTMLDivElement> | null
    prependItems?: SearchDialogItem<any>[]
    items?: SearchDialogItem<any>[]
    appendItems?: SearchDialogItem<any>[]
  }>()

  const {
    defaultSearchContext,
    selectedSearchContext,
    searchResults,
    isSearching,
    isSearchInputEmpty,
    allowedSearchContextsMetadata,
    callSearch,
    setSelectedSearchContext,
    searchInputValue
  } = useSearch()

  const { recentSearchItems } = useRecentSearch()

  const searchDialogContentRef = ref<InstanceType<
    typeof HTMLDivElement
  > | null>(props.contentContainerRef)

  function getRecentSearchDialogItems(
    items: RecentSearch[]
  ): SearchDialogItem<RecentSearch>[] {
    let result: SearchDialogItem<RecentSearch>[] = items.map((item) => ({
      id: item.searchString + item.context,
      title: item.searchString,
      focusable: true,
      props: item,
      component: RecentSearchItem,
      selected: (item) => {
        callSearch(item.searchString, item.context)
      }
    }))
    if (selectedSearchContext.value !== 'global') {
      result = result.filter(
        (item) => item.props?.context === selectedSearchContext.value
      )
    }
    return result.slice(0, 5)
  }

  function getContextSearchDialogItems(): SearchDialogItem<GlobalSearchContextItemProps>[] {
    const result: SearchDialogItem<GlobalSearchContextItemProps>[] = []
    Object.entries(allowedSearchContextsMetadata.value).forEach(
      ([key, value]) => {
        if (
          !searchInputValue.value &&
          selectedSearchContext.value === defaultSearchContext &&
          key === defaultSearchContext
        ) {
          return
        }
        result.push({
          id: key,
          title: value.title,
          focusable: true,
          props: { context: key as SearchContextType },
          component: GlobalSearchContextItem,
          selected: (item) => {
            if (item.context === defaultSearchContext) {
              callSearch(searchInputValue.value, item.context)
            } else {
              setSelectedSearchContext(item.context)
            }
          }
        })
      }
    )
    return result
  }

  const searchDialogItems = computed((): SearchDialogItem<any>[] => {
    const result: SearchDialogItem<any>[] = []

    if (props.items) {
      return props.items
    }

    const isDefaultSearchContext =
      selectedSearchContext.value === defaultSearchContext
    if (isSearchInputEmpty.value) {
      // Recent search items
      result.push({
        id: 'recent-search-items-header',
        title: 'Recent searches',
        focusable: false
      })
      result.push(...getRecentSearchDialogItems(recentSearchItems.value))
    }
    if (isDefaultSearchContext && !searchResults.value && !isSearching.value) {
      // Default context searches
      result.push({
        id: 'context-searches-header',
        title: 'Context searches',
        focusable: false
      })
      result.push(...getContextSearchDialogItems())
    }
    return [
      ...(props.prependItems ?? []),
      ...result,
      ...(props.appendItems ?? [])
    ]
  })

  const keyboardNavigation =
    globalUtils.composables.useDetachedKeyboardNavigation(
      searchDialogContentRef,
      searchDialogItems,
      (item) => item.selected?.(item.props),
      {
        focusableProperty: {
          key: 'focusable',
          enabledValue: true
        }
      }
    )

  onMounted(() => {
    keyboardNavigation.addEventListeners()
    keyboardNavigation.focusFirstItem()
  })

  onUnmounted(() => {
    keyboardNavigation.removeEventListeners()
  })

  defineExpose({
    keyboardNavigation
  })
</script>

<template>
  <template v-for="(item, index) in searchDialogItems" :key="item.id + index">
    <slot name="item" :item="item" :index="index">
      <component
        :is="item.component"
        v-if="item.component"
        v-bind="item.props"
        class="hover:bg-accent focus:bg-accent cursor-pointer"
        :class="{
          'bg-accent inset-ring-primary inset-ring-1':
            keyboardNavigation.focusedIndex.value === index
        }"
        @click="item.selected?.(item.props)"
      >
        <template
          #actions
          v-if="keyboardNavigation.focusedIndex.value === index"
        >
          <GatKeyboardShortcuts class="ml-auto" shortcut="Enter" />
        </template>
      </component>
      <div
        v-else
        class="border-b bg-gray-50 px-4 py-2 text-xs font-medium"
        :class="{
          'bg-accent': keyboardNavigation.focusedIndex.value === index
        }"
      >
        {{ item.title }}
      </div>
    </slot>
  </template>
</template>
